import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, User, Loader2, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ReactMarkdown from 'react-markdown';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'human_agent';
  content: string;
  createdAt: Date;
}

const FINGERPRINT_STORAGE_KEY = 'itdukes_fingerprint';
const CONVERSATION_ID_KEY = 'itdukes_support_conversation_id';

export const SupportChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [waitingForHuman, setWaitingForHuman] = useState(false);
  const [showHumanButton, setShowHumanButton] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const getFingerprint = (): string => {
    let fingerprint = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (!fingerprint) {
      const components = [
        navigator.userAgent,
        navigator.language,
        screen.width,
        screen.height,
        new Date().getTimezoneOffset(),
      ];
      let hash = 0;
      const str = components.join('|');
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      fingerprint = Math.abs(hash).toString(36);
      localStorage.setItem(FINGERPRINT_STORAGE_KEY, fingerprint);
    }
    return fingerprint;
  };

  // Load existing conversation
  useEffect(() => {
    const savedConversationId = sessionStorage.getItem(CONVERSATION_ID_KEY);
    if (savedConversationId) {
      setConversationId(savedConversationId);
      loadConversation(savedConversationId);
    }
  }, []);

  const loadConversation = async (convId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('support-chat', {
        body: { action: 'get-messages', conversationId: convId },
      });

      if (!error && data?.messages) {
        setMessages(data.messages.map((m: any) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: new Date(m.created_at),
        })));
        setWaitingForHuman(data.requiresHuman || false);
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages when waiting for human
  useEffect(() => {
    if (!waitingForHuman || !conversationId) return;

    const interval = setInterval(() => {
      loadConversation(conversationId);
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [waitingForHuman, conversationId]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    // Optimistically add user message
    const tempId = `temp-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tempId,
      role: 'user',
      content: userMessage,
      createdAt: new Date(),
    }]);

    try {
      const { data, error } = await supabase.functions.invoke('support-chat', {
        body: {
          action: 'send-message',
          conversationId,
          fingerprint: getFingerprint(),
          message: userMessage,
        },
      });

      if (error) throw error;

      // Save conversation ID if new
      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId);
        sessionStorage.setItem(CONVERSATION_ID_KEY, data.conversationId);
      }

      // Add assistant response
      if (data.response) {
        setMessages(prev => [...prev, {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.response,
          createdAt: new Date(),
        }]);
      }

      // Show human button if bot suggests it
      if (data.suggestHuman) {
        setShowHumanButton(true);
      }

      if (data.requiresHuman) {
        setWaitingForHuman(true);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      toast({
        title: "Failed to send message",
        description: "Please try again.",
        variant: "destructive",
      });
      // Remove optimistic message
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors group"
        aria-label="Open support chat"
      >
        <img 
          src="/favicon.png" 
          alt="IT Dukes" 
          className="w-8 h-8 rounded-full"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
        <MessageCircle className="w-6 h-6 text-primary-foreground hidden" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[360px] h-[520px] bg-card rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-primary p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden">
            <img 
              src="/favicon.png" 
              alt="IT Dukes" 
              className="w-8 h-8"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <div>
            <h3 className="font-semibold text-primary-foreground">IT Dukes Support</h3>
            <p className="text-xs text-primary-foreground/80">We're here to help</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center hover:bg-primary-foreground/30 transition-colors"
        >
          <X className="w-4 h-4 text-primary-foreground" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
              <img 
                src="/favicon.png" 
                alt="IT Dukes" 
                className="w-10 h-10"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            <h4 className="font-medium text-foreground mb-1">Hi there! 👋</h4>
            <p className="text-sm text-muted-foreground mb-4">
              I'm your IT Dukes assistant. How can I help you today?
            </p>
            <div className="space-y-2 text-left">
              <p className="text-xs text-muted-foreground font-medium">Quick questions:</p>
              {[
                "How do I check my email authentication?",
                "Why are my emails going to spam?",
                "How does the blacklist checker work?"
              ].map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => setInputValue(suggestion)}
                  className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`flex items-start gap-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
              {message.role !== 'user' && (
                <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${
                  message.role === 'human_agent' 
                    ? 'bg-green-100 dark:bg-green-900/30' 
                    : 'bg-primary/10'
                }`}>
                  {message.role === 'human_agent' ? (
                    <User className="w-3 h-3 text-green-600 dark:text-green-400" />
                  ) : (
                    <Bot className="w-3 h-3 text-primary" />
                  )}
                </div>
              )}
              <div
                className={`rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : message.role === 'human_agent'
                      ? 'bg-green-100 dark:bg-green-900/30 text-foreground rounded-bl-sm'
                      : 'bg-muted text-foreground rounded-bl-sm'
                }`}
              >
                {message.role === 'human_agent' && (
                  <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 mb-1">
                    <User className="w-3 h-3" />
                    Support Agent
                  </div>
                )}
                {message.role === 'user' ? (
                  <p className="text-base leading-relaxed whitespace-pre-wrap">{message.content}</p>
                ) : (
                  <div className="prose prose-sm dark:prose-invert max-w-none text-base leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:my-2 [&>ol]:my-2 [&>h1]:text-lg [&>h2]:text-base [&>h3]:text-base [&>h1]:font-bold [&>h2]:font-semibold [&>h3]:font-medium [&>a]:text-primary [&>a]:underline">
                    <ReactMarkdown
                      components={{
                        code: ({ node, className, children, ...props }) => {
                          const isInline = !className;
                          if (isInline) {
                            return (
                              <code className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                                {children}
                              </code>
                            );
                          }
                          return (
                            <div className="my-2 rounded-lg overflow-hidden">
                              <pre className="bg-muted/80 dark:bg-muted/50 p-3 overflow-x-auto text-sm">
                                <code className="font-mono text-foreground" {...props}>
                                  {children}
                                </code>
                              </pre>
                            </div>
                          );
                        },
                        pre: ({ children }) => <>{children}</>,
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="w-3 h-3 text-primary" />
              </div>
              <div className="bg-muted rounded-2xl px-4 py-2 rounded-bl-sm">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {waitingForHuman && (
        <div className="px-4 pb-2">
          <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs text-center py-2 rounded-lg flex items-center justify-center gap-2">
            <User className="w-3 h-3" />
            A human agent will respond soon
          </div>
        </div>
      )}

      {showHumanButton && !waitingForHuman && (
        <div className="px-4 pb-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/20"
            onClick={async () => {
              try {
                const { error } = await supabase.functions.invoke('support-chat', {
                  body: {
                    action: 'request-human',
                    conversationId,
                    fingerprint: getFingerprint(),
                  },
                });
                if (!error) {
                  setWaitingForHuman(true);
                  setShowHumanButton(false);
                  toast({
                    title: "Request sent",
                    description: "A human agent will respond shortly.",
                  });
                }
              } catch (err) {
                console.error('Failed to request human:', err);
              }
            }}
          >
            <User className="w-3 h-3 mr-1" />
            Talk to a human agent
          </Button>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-border">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage();
          }}
          className="flex gap-2"
        >
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button type="submit" size="icon" disabled={isLoading || !inputValue.trim()}>
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};
